import type {
  BatchJob,
  PubmedIngestItemResult,
  PubmedIngestParams,
} from "../../../../../lib/corpusApi";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import {
  useBatchJobItemRetry,
  type UseBatchJobItemRetryResult,
} from "../../panel/useBatchJobItemRetry";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import {
  useDocumentDetailTarget,
  type UseDocumentDetailTargetResult,
} from "../../panel/useDocumentDetailTarget";
import {
  useRejectionCodeFilter,
  type UseRejectionCodeFilterResult,
} from "../../panel/useRejectionCodeFilter";
import {
  PUBMED_INGEST_TABS,
  type PubmedIngestTabConfig,
  type PubmedIngestTabKey,
} from "./pubmed_ingest_tabs";

/** One list row: the raw item plus its pubmed_ingest result. A failed item has none. */
export type PubmedIngestRow = BatchJobItemRow<PubmedIngestItemResult>;

export interface UsePubmedIngestPanelResult {
  /** What the job was started with: the query it ingests and its limit. */
  params: PubmedIngestParams;
  tabs: UseBatchJobTabsResult<PubmedIngestTabKey, PubmedIngestTabConfig>;
  filters: UseRejectionCodeFilterResult;
  list: UseBatchJobItemListResult<PubmedIngestItemResult>;
  detail: UseDocumentDetailTargetResult<PubmedIngestItemResult>;
  /** Puts a failed item back in line to be ingested again. */
  retry: UseBatchJobItemRetryResult;
}

const narrowPubmedIngestResult = narrowByOutcome<PubmedIngestItemResult>(
  new Set(["ingested", "needs_review", "rejected", "previously_rejected", "existing"])
);

/** The id the corpus holds an item's article under, once it is there — what a document detail
 *  dialog opens by. Null for an article that never became a document. */
export function documentIdOf(row: PubmedIngestRow): string | null {
  return row.result?.sourceId ?? null;
}

function toDocumentTarget(row: PubmedIngestRow) {
  const id = documentIdOf(row);
  return id ? { id, title: row.item.subjectTitle, url: row.item.subjectUrl } : null;
}

/** Drives the pubmed_ingest batch job panel: one paginated list per outcome, read-only except for
 *  retrying a failed item. `onJobChanged` refetches the job when a retry changes its status. */
export function usePubmedIngestPanel(
  job: BatchJob,
  onJobChanged?: () => Promise<void>
): UsePubmedIngestPanelResult {
  const tabs = useBatchJobTabs(PUBMED_INGEST_TABS, "ingested");
  const filters = useRejectionCodeFilter(tabs.activeTab);

  const list = useBatchJobItemList({
    job,
    filters: {
      ...tabs.tab.apiFilters,
      ...(filters.rejectionCode ? { pubmedIngest: { rejectionCode: filters.rejectionCode } } : {}),
    },
    resetKey: `${tabs.activeTab}:${filters.rejectionCode ?? ""}`,
    narrow: narrowPubmedIngestResult,
  });

  return {
    params: job.params as unknown as PubmedIngestParams,
    tabs,
    filters,
    list,
    detail: useDocumentDetailTarget(toDocumentTarget),
    retry: useBatchJobItemRetry({ job, refresh: list.refresh, onJobChanged }),
  };
}
